import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { JSX } from 'react';
import { Control, Controller, FieldErrors, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toTimeInputValue } from '../../library/dateTimeHelper';
import { ClinicProgramDraft, IProgramDay, WEEKDAY_LABELS } from '../../library/models/clinicProgram';
import ResourcesIds from '../../resources/ResourcesIds';
import { AppTimePicker } from '../pickers/AppTimePicker';
import { ProgramWeekFormValues } from './programWeekSchema';
import { dayHoursStyles, dayLabelStyles, dayListStyles, dayRowStyles, dayToggleStyles, timeFieldStyles } from './programWeekEditor.styles';

/**
 * The words one editor uses for a day being on or off.
 *
 * A clinic *opens*; a person is *available*. Same seven rows and the same rule, but calling a
 * therapist's Tuesday "closed" describes a building rather than them -- so the vocabulary is a
 * prop rather than something the component decides, and both sets are declared below so there
 * is one place to read what each screen says.
 */
export interface ProgramWeekCopy {
    /** The switch's label when the day is on, and when it is off. */
    on: string;
    off: string;
    /** The switch's accessible name, interpolated with `{{day}}`. */
    toggle: string;
    /** The two time fields, each prefixed with the day's name. */
    from: string;
    until: string;
}

/** What the clinic's own hours are called: the building is open or closed. */
export const CLINIC_WEEK_COPY: ProgramWeekCopy = {
    on: ResourcesIds.WORKING_HOURS_OPEN_LABEL,
    off: ResourcesIds.WORKING_HOURS_CLOSED_LABEL,
    toggle: ResourcesIds.WORKING_HOURS_DAY_TOGGLE_LABEL,
    from: ResourcesIds.CLINIC_WORKING_HOURS_START_LABEL,
    until: ResourcesIds.CLINIC_WORKING_HOURS_END_LABEL,
};

/** What a therapist's own hours are called: a person is available, or is not. */
export const THERAPIST_WEEK_COPY: ProgramWeekCopy = {
    on: ResourcesIds.MY_SCHEDULE_AVAILABLE_LABEL,
    off: ResourcesIds.MY_SCHEDULE_UNAVAILABLE_LABEL,
    toggle: ResourcesIds.MY_SCHEDULE_DAY_TOGGLE_LABEL,
    from: ResourcesIds.MY_SCHEDULE_START_LABEL,
    until: ResourcesIds.MY_SCHEDULE_END_LABEL,
};

interface ProgramWeekEditorProps {
    control: Control<ProgramWeekFormValues>;
    setValue: UseFormSetValue<ProgramWeekFormValues>;
    errors: FieldErrors<ProgramWeekFormValues>;
    /** The live values, watched by the caller -- `fields` would not re-render on a toggle. */
    days: ClinicProgramDraft;
    /** Whose hours these are, in words. Required: there is no sensible default for both. */
    copy: ProgramWeekCopy;
    /**
     * The clinic's own week, when this editor is capped by one. Only the therapist's is:
     * the clinic's hours are bounded by nothing.
     */
    bounds?: IProgramDay[];
}

/**
 * Seven days, each either shut or open between two times.
 *
 * One component for the clinic's hours and for a therapist's own, because the two screens ask
 * the same question of the same shape and differ only in what caps them and what they call it.
 * A clinic opens; a person is available -- so the words arrive as `copy` (see
 * {@link CLINIC_WEEK_COPY} and {@link THERAPIST_WEEK_COPY}) rather than being decided here. What is
 * genuinely shared is not the layout -- it is the two traps below, and a second copy of
 * them is the drift that would eventually leave one screen accessible and the other not:
 *
 *   * the per-day `aria-label` on every switch and every time field, so fourteen controls
 *     are told apart by what they are rather than by position. A screen reader, and every
 *     test in this repo, finds them by "Wednesday — opens at";
 *   * `slotProps` rather than `inputProps` on the switch. MUI v7 ignores the latter here and
 *     it fails *silently*, leaving seven switches nothing can tell apart. `role` has to be
 *     restored by hand because these props replace the switch's own rather than merging.
 *
 * A closed day keeps its times and only disables the fields, rather than clearing them --
 * somebody shutting Saturday and changing their mind gets last week's hours back. The API
 * stores them for the same reason.
 *
 * Presentational: it holds no query, no mutation and no copy of its own beyond the day
 * names. Titles, descriptions, saving and what to do about a failure belong to the panel
 * that renders it, because those are where the two screens genuinely differ.
 */
export const ProgramWeekEditor = (props: ProgramWeekEditorProps): JSX.Element => {
    const { t: translate } = useTranslation();

    /** The clinic's hours for a day, when this editor is capped. */
    const capFor = (day: IProgramDay): IProgramDay | undefined =>
        props.bounds?.find(entry => entry.dayOfWeek === day.dayOfWeek);

    return (
        <Box sx={dayListStyles}>
            {props.days.map((day: IProgramDay, index: number) => {
                const dayName: string = translate(WEEKDAY_LABELS[day.dayOfWeek]);
                const cap: IProgramDay | undefined = capFor(day);

                /*
                 * A day nobody can work is not a choice to offer. The switch is disabled
                 * rather than hidden, and the row says why -- a missing Sunday would read
                 * as a bug, and a Sunday that silently refuses to turn on reads as a worse
                 * one.
                 */
                const closedAtTheClinic: boolean = !!props.bounds && !cap?.open;

                // Only the row that is wrong is marked, so seven red fields never stand in
                // for one mistyped hour. The schema hangs every message off `closesAt`,
                // which is where the panel already shows one.
                const hoursError: string | undefined = props.errors.days?.[index]?.closesAt?.message;
                const hasBadHours: boolean = !!hoursError;

                return (
                    <Box key={day.dayOfWeek} sx={dayRowStyles}>
                        <Typography sx={dayLabelStyles}>{dayName}</Typography>

                        <FormControlLabel
                            sx={dayToggleStyles}
                            disabled={closedAtTheClinic}
                            control={
                                <Switch
                                    checked={day.open && !closedAtTheClinic}
                                    disabled={closedAtTheClinic}
                                    slotProps={{
                                        input: {
                                            role: 'switch',
                                            'aria-label': translate(
                                                props.copy.toggle, { day: dayName }
                                            ),
                                        },
                                    }}
                                    onChange={(_event, checked: boolean) =>
                                        props.setValue(`days.${index}.open`, checked,
                                            { shouldValidate: true })
                                    }
                                />
                            }
                            label={translate(
                                // The one label that stays the clinic's own words even on the
                                // therapist's screen, because it is a fact about the clinic:
                                // the door is shut, so nobody is available whatever they say.
                                closedAtTheClinic
                                    ? ResourcesIds.MY_SCHEDULE_CLINIC_CLOSED_HINT
                                    : day.open ? props.copy.on : props.copy.off
                            )}
                        />

                        <Box sx={dayHoursStyles}>
                            <Controller
                                name={`days.${index}.opensAt`}
                                control={props.control}
                                render={({ field }) => (
                                    <AppTimePicker
                                        label={`${dayName} — ${translate(props.copy.from)}`}
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        name={field.name}
                                        inputRef={field.ref}
                                        // A clinic opens on the quarter hour.
                                        minutesStep={15}
                                        // Bounds what is *offered*, never what may be
                                        // typed -- the schema and the API are what refuse
                                        // an hour outside the clinic's.
                                        minTime={cap && toTimeInputValue(cap.opensAt)}
                                        maxTime={cap && toTimeInputValue(cap.closesAt)}
                                        sx={timeFieldStyles}
                                        disabled={!day.open || closedAtTheClinic}
                                        error={day.open && hasBadHours}
                                    />
                                )}
                            />

                            <Controller
                                name={`days.${index}.closesAt`}
                                control={props.control}
                                render={({ field }) => (
                                    <AppTimePicker
                                        label={`${dayName} — ${translate(props.copy.until)}`}
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        name={field.name}
                                        inputRef={field.ref}
                                        minutesStep={15}
                                        minTime={cap && toTimeInputValue(cap.opensAt)}
                                        maxTime={cap && toTimeInputValue(cap.closesAt)}
                                        sx={timeFieldStyles}
                                        disabled={!day.open || closedAtTheClinic}
                                        error={day.open && hasBadHours}
                                        helperText={day.open ? hoursError : undefined}
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};
